import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import type { CampaignStatusBreakdown as StatusBreakdown } from '../../types/dashboard';

interface CampaignStatusBreakdownProps {
  data: StatusBreakdown;
  loading?: boolean;
  error?: string;
}

const CampaignStatusBreakdown: React.FC<CampaignStatusBreakdownProps> = ({
  data,
  loading = false,
  error
}) => {
  const statusConfig = {
    active: { label: 'Aktif', color: '#10B981' },
    paused: { label: 'Duraklatıldı', color: '#F59E0B' },
    draft: { label: 'Taslak', color: '#6B7280' },
    completed: { label: 'Tamamlandı', color: '#3B82F6' }
  };

  const chartData = Object.entries(data).map(([status, count]) => ({
    name: statusConfig[status as keyof typeof statusConfig]?.label || status,
    value: count,
    color: statusConfig[status as keyof typeof statusConfig]?.color || '#6B7280'
  })).filter(item => item.value > 0);

  const total = Object.values(data).reduce((sum, count) => sum + count, 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow">
          <p className="font-medium">{data.name}</p>
          <p className="text-sm text-gray-600">
            {data.value} kampanya ({((data.value / total) * 100).toFixed(1)}%)
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Kampanya Durumu Dağılımı</h3>
          <div className="h-64 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Kampanya Durumu Dağılımı</h3>
          <div className="text-center py-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Kampanya Durumu Dağılımı</h3>
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">Henüz kampanya bulunmuyor</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Kampanya Durumu Dağılımı</h3>
        
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Status breakdown list */}
        <div className="mt-6 grid grid-cols-2 gap-4">
          {Object.entries(data).map(([status, count]) => {
            const config = statusConfig[status as keyof typeof statusConfig];
            if (!config || count === 0) return null;
            
            const percentage = total > 0 ? (count / total) * 100 : 0;
            
            return (
              <div key={status} className="flex items-center space-x-3">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: config.color }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{config.label}</p>
                  <p className="text-xs text-gray-500">
                    {count} kampanya ({percentage.toFixed(1)}%)
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Total */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-900">Toplam Kampanya</span>
            <span className="text-sm font-semibold text-gray-900">{total}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignStatusBreakdown; 